import { PeoplePicker, PersonType } from "@microsoft/mgt-react";
import { isEqual } from "lodash";
import { Dispatch, FC, Fragment, useCallback, useMemo } from "react";
import { approverType } from "../../../../../../constants/itCodeRequestConstants";
import { useSelector } from "../../../../../../store/store";
import InformationList, { ColumnContent, LeftRightContent, ListContent } from "../../../../components/InformationList";
import TableModal from "../../../../components/detail";
import { useProcess } from "../../../../process-context";
import { FileAttachmentCard } from "../../../../upload";
import CommentSection from "../../../commentsection/comment-sections";
import { tableChangeColumns, tableColumns } from "./table-column";

interface Props {
  setAssignList: Dispatch<any>;
  setITHeadSelected: Dispatch<any>;
  setITHeadName: Dispatch<any>;
}

const FormContent: FC<Props> = (props) => {
  const { setAssignList, setITHeadSelected, setITHeadName } = props;

  const { Request, approvalStep, approvalType, comment, setComment } = useProcess();

  const { fields } = Request;

  /**
   * Selector
   */
  const user = useSelector((state) => state.user);

  const {
    eDescription,
    eRequestType,
    eDeptHeadComment,
    eITLeadComment,
    eITHeadComment,
    eAssigneeComment,
    eApprovedBy,
    eApprovedByITLead,
    eApprovedByITHead,
    eAssignee,
  } = fields;

  const onEmployeeChange = useCallback(
    (selectedOptions: any) => {
      const assignedList = selectedOptions?.target?.selectedPeople;
      setAssignList(assignedList);
    },
    [setAssignList],
  );

  const onITHeadChange = useCallback(
    (selectedOptions: any) => {
      setITHeadSelected(selectedOptions && selectedOptions?.target?.selectedPeople[0]?.userPrincipalName);
      setITHeadName(selectedOptions ? selectedOptions?.target?.selectedPeople[0]?.displayName : null);
    },
    [setITHeadName, setITHeadSelected],
  );

  const comments = useMemo(() => {
    return [
      {
        hidden: !eDeptHeadComment,
        content: `Comment của ${eApprovedBy.split("|")[1]}: ${eDeptHeadComment}`,
      },
      {
        hidden: !eITLeadComment,
        content: `Comment của ${eApprovedByITLead.split("|")[1]}: ${eITLeadComment}`,
      },
      {
        hidden: !eITHeadComment,
        content: `Comment của ${eApprovedByITHead.split("|")[1]}: ${eITHeadComment}`,
      },
      {
        hidden: !eAssigneeComment,
        content: `Comment của ${eAssignee.split("|")[1]}: ${eAssigneeComment}`,
      },
      {
        hidden: approvalStep !== approvalType,
        content: (
          <div className="input">
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} />
          </div>
        ),
      },
    ];
  }, [
    eDeptHeadComment,
    eApprovedBy,
    eITLeadComment,
    eApprovedByITLead,
    eITHeadComment,
    eApprovedByITHead,
    eAssigneeComment,
    eAssignee,
    approvalStep,
    approvalType,
    comment,
    setComment,
  ]);

  const smColData = useMemo(() => {
    return [
      {
        content: (
          <LeftRightContent
            left="Yêu cầu:"
            right={
              <strong>
                {Request.fields.eRequestType === "provide"
                  ? "Cấp mới"
                  : Request.fields.eRequestType === "change"
                  ? "Thay đổi"
                  : "Xoá"}
              </strong>
            }
          />
        ),
      },
      {
        content: (
          <LeftRightContent left="Người yêu cầu:" right={<strong>{Request.createdBy.user.displayName}</strong>} />
        ),
      },
      {
        content: (
          <LeftRightContent
            left="SĐT Người yêu cầu:"
            right={<strong>{Request.fields.ePhoneNumber}</strong>}
            smColumn={false}
          />
        ),
      },
      {
        content: <LeftRightContent left="Cơ quan/Đơn vị:" right={<strong>{Request.fields.eDepartment}</strong>} />,
      },
      {
        hidden: {
          sm: isEqual(Request.fields.eUnit, Request.fields.eDepartment),
        },
        content: <LeftRightContent left="Đơn vị:" right={<strong>{Request.fields.eUnit}</strong>} />,
      },
      {
        hidden: {
          sm: isEqual(Request.fields.eUnit, Request.fields.eUnion),
        },
        content: <LeftRightContent left="Đội/Tổ:" right={<strong>{Request.fields.eUnion}</strong>} />,
      },
      {
        content: <LeftRightContent left="Mô tả:" right={eDescription} />,
      },
    ];
  }, [eDescription, Request]);

  const fullColData = useMemo(() => {
    return [
      {
        content: (
          <TableModal
            data={JSON.parse(Request.fields.eContent)}
            columns={eRequestType === "provide" ? tableColumns : tableChangeColumns}
          />
        ),
      },
      {
        content: (
          <Fragment>
            Tệp đính kèm
            <div className="mt-2 d-flex flex-column">
              <FileAttachmentCard
                listFile={Request.fields.eFileContent ? JSON.parse(Request.fields.eFileContent) : []}
              />
            </div>
          </Fragment>
        ),
      },
      {
        content: (
          <ListContent
            label="Comments"
            data={comments}
            getItemHidden={(item) => item.hidden}
            renderItemContent={(item) => item.content}
          />
        ),
      },
      {
        content: (
          <Fragment>
            Thông tin trao đổi
            <div className="mt-2">
              <CommentSection comments={[]} data={Request} user={user} id={1} className="mb-4" />
            </div>
          </Fragment>
        ),
      },
      {
        hidden:
          approvalType !== approverType.itlead ||
          approvalStep !== approvalType ||
          Request.fields.eDepartment === "Phòng Công nghệ thông tin",
        content: (
          <ColumnContent
            label="Đơn vị quản lý chương trình"
            content={
              <div className="people_picker-custom-detail">
                <PeoplePicker
                  className="people-picker"
                  placeholder="Vui lòng chọn đơn vị quản lý chương trình"
                  type={PersonType.any}
                  selectionChanged={onITHeadChange}
                />
              </div>
            }
          />
        ),
      },
      {
        hidden: approvalType !== approverType.itlead || approvalStep !== approvalType,
        content: (
          <ColumnContent
            label="Cán bộ/Chuyên viên CNTT thực hiện"
            content={
              <div className="people_picker-custom-detail">
                <PeoplePicker
                  className="people-picker"
                  placeholder="Vui lòng chọn cán bộ thực hiện"
                  type={PersonType.any}
                  selectionChanged={onEmployeeChange}
                />
              </div>
            }
          />
        ),
      },
    ];
  }, [approvalStep, approvalType, comments, Request, onEmployeeChange, onITHeadChange, user]);

  return (
    <Fragment>
      <InformationList
        size="sm-col"
        data={smColData}
        renderContent={(item) => item.content}
        getItemHidden={(item) => item.hidden}
      />
      <InformationList
        size="full-col"
        data={fullColData}
        getItemHidden={(item) => item.hidden}
        renderContent={(item) => item.content}
      />
    </Fragment>
  );
};

export default FormContent;
